import { CopyIcon, PencilIcon, RotateCcwIcon } from 'lucide-react';
import { toast } from 'sonner';
import { MessageAction, MessageActions } from '@/components/ai-elements/message';
import type { ChatUIMessage } from '../types';
import { ChatMessageFeedback } from './ChatMessageFeedback';

type ChatMessageActionsProps = {
  canRetry: boolean;
  /** False while the answer streams: it is not persisted yet. */
  canGiveFeedback?: boolean;
  message: ChatUIMessage;
  onEdit: (message: ChatUIMessage) => void;
  onRetry: () => void;
};

function getTextContent(message: ChatUIMessage) {
  return message.parts
    .filter((part): part is Extract<ChatUIMessage['parts'][number], { type: 'text' }> => part.type === 'text')
    .map((part) => part.text)
    .join('\n')
    .trim();
}

export function ChatMessageActions({ canGiveFeedback = false, canRetry, message, onEdit, onRetry }: ChatMessageActionsProps) {
  const textContent = getTextContent(message);
  const isUser = message.role === 'user';

  return (
    <MessageActions className="flex-wrap opacity-100 transition-opacity duration-150 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
      <MessageAction
        onClick={async () => {
          if (!textContent) {
            toast.error('Aucun texte à copier pour ce message.');
            return;
          }

          await navigator.clipboard.writeText(textContent);
          toast.success('Message copié dans le presse-papiers.');
        }}
        className="size-8 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
        label="Copier le message"
        tooltip="Copier le message"
      >
        <CopyIcon className="size-3.5" />
      </MessageAction>

      {isUser ? (
        <MessageAction
          onClick={() => onEdit(message)}
          className="size-8 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          label="Modifier le message"
          tooltip="Modifier le message"
        >
          <PencilIcon className="size-3.5" />
        </MessageAction>
      ) : null}

      {!isUser && canRetry ? (
        <MessageAction
          onClick={onRetry}
          className="size-8 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          label="Régénérer la réponse"
          tooltip="Régénérer la réponse"
        >
          <RotateCcwIcon className="size-3.5" />
        </MessageAction>
      ) : null}

      {!isUser && canGiveFeedback ? <ChatMessageFeedback messageId={message.id} /> : null}
    </MessageActions>
  );
}
